/**
 * Gravação, transcrição e o estado do processamento — a metade da tela 7 que fala de material.
 *
 * **O caminho manual vem primeiro, e não é um remendo.** A spec §5.5 desenha um pipeline
 * (gravação → áudio → blocos → fila → transcrição → resumo) que hoje não roda: não há credencial
 * do `geo-ai-gateway`, `ffmpeg` não está no container da API e o servidor é dividido com outro
 * produto. O portal construiu o pipeline atrás de adaptador, com estado real no banco e fila que
 * funciona — e recusa enfileirar enquanto não houver quem drene, porque fila sem worker é mídia
 * presa para sempre. Esta tela mostra isso na ordem em que é verdade: o que funciona em cima, com
 * a moldura da marca; o que não funciona embaixo, apagado, com o motivo escrito.
 *
 * **Um vídeo de 1h30 não cabe, e isso está dito antes do envio.** O teto é 100 MB porque o
 * `client_max_body_size` do nginx é 100M; um teto maior aqui prometeria um upload que morre no
 * proxy. O que cabe é o áudio extraído fora ou — melhor — a transcrição pronta que o Meet e o
 * Teams exportam.
 *
 * **Colar o texto é o mesmo ato de subir o arquivo.** A API recebe transcrição por `multipart`, e
 * o texto colado vira um `.txt` aqui mesmo: quem tem a transcrição na área de transferência não
 * deveria precisar salvá-la em disco para o portal aceitá-la.
 *
 * **`processando` para sempre é o estado que ninguém enxerga**, e por isso ele tem nome nesta
 * tela: passados 30 minutos — o mesmo teto que o worker usa para varrer órfãs — a linha aparece
 * como travada, e não como "processando" igual a uma que começou agora.
 *
 * **Tudo isto é do lado de dentro.** A listagem exige `ata:ver_interno` e a transcrição sai só no
 * detalhe de um artefato: ela é a conversa inteira do cliente e a matéria-prima de onde a nota
 * interna foi extraída. Entregá-la ao cliente derrotaria `visivel_ao_cliente` numa consulta só.
 */

import { useRef, useState } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  EXTENSOES_DE_AUDIO,
  EXTENSOES_DE_GRAVACAO,
  EXTENSOES_DE_TRANSCRICAO,
  TAMANHO_MAXIMO_DA_MIDIA_EM_BYTES,
  arquivoDeTranscricaoColada,
  formatarQuando,
  formatarTamanho,
  midiaTravada,
  rotuloDaEtapa,
  rotuloDoEstadoDaMidia,
  rotuloDoTipoDeMidia,
  urlDoArquivoDaMidia,
  useEnfileirarDerivacao,
  useEnviarMidia,
  useMidia,
  useMidias,
  useRemoverMidia,
  useReprocessarMidia,
  type EstadoDoPipeline,
  type Midia,
} from "../../nucleo/reuniao";

export interface PropsDaGravacao {
  reuniaoId: string;
  /** `ata:ver_interno` — sem ela a listagem de mídia nem é pedida. */
  veNotaInterna: boolean;
  /** `ata:editar` — quem sobe, enfileira, reprocessa e remove. */
  podeEditar: boolean;
}

export function GravacaoDaReuniao({ reuniaoId, veNotaInterna, podeEditar }: PropsDaGravacao) {
  const midias = useMidias(reuniaoId, veNotaInterna);

  if (!veNotaInterna) {
    return (
      <Aviso tom="marca" data-testid="sem-acesso-a-midia">
        <b>A gravação e a transcrição ficam com quem vê as notas internas.</b> Elas trazem tudo o
        que foi dito na reunião; o que sai para o cliente é a ata publicada.
      </Aviso>
    );
  }

  if (midias.isPending) {
    return (
      <p className="lead" role="status">
        Carregando o material da reunião…
      </p>
    );
  }

  if (midias.isError) {
    return (
      <Aviso tom="erro" data-testid="falha-das-midias">
        {mensagemDoConsole(midias.error)}
      </Aviso>
    );
  }

  return (
    <>
      <CaminhoManual reuniaoId={reuniaoId} podeEditar={podeEditar} />
      <ProcessamentoAutomatico pipeline={midias.data.pipeline} />
      <ListaDeMidias
        reuniaoId={reuniaoId}
        midias={midias.data.midias}
        pipeline={midias.data.pipeline}
        podeEditar={podeEditar}
      />
    </>
  );
}

/* ---------------------------------------------------------------- o caminho que funciona */

function CaminhoManual({
  reuniaoId,
  podeEditar,
}: {
  reuniaoId: string;
  podeEditar: boolean;
}) {
  if (!podeEditar) {
    return (
      <Aviso tom="marca" className="mb-4" data-testid="sem-permissao-de-subir-midia">
        <b>Subir gravação ou transcrição é de quem escreve a ata</b> — é o mesmo trabalho.
      </Aviso>
    );
  }

  return (
    <div className="grade-do-caminho-manual mb-4">
      <TranscricaoPronta reuniaoId={reuniaoId} />
      <ArquivoDaReuniao reuniaoId={reuniaoId} />
    </div>
  );
}

function TranscricaoPronta({ reuniaoId }: { reuniaoId: string }) {
  const [texto, setTexto] = useState("");
  const [duracao, setDuracao] = useState("");
  const arquivo = useRef<HTMLInputElement>(null);
  const envio = useEnviarMidia(reuniaoId);

  const duracaoSeg = duracao.trim() === "" ? null : Number(duracao) * 60;

  return (
    <Cartao
      className="cartao--caminho-que-funciona"
      titulo="Transcrição pronta"
      data-testid="enviar-transcricao"
    >
      <p className="cartao__texto">
        É por aqui que uma reunião de 1h30 entra no portal: o arquivo de legenda que o Meet e o
        Teams exportam (<code>.vtt</code>, <code>.srt</code>, <code>.txt</code>), ou o texto
        colado.
      </p>

      <Campo
        rotulo="Arquivo de transcrição"
        type="file"
        accept={EXTENSOES_DE_TRANSCRICAO.join(",")}
        ref={arquivo}
        ajuda={`Aceita ${EXTENSOES_DE_TRANSCRICAO.join(", ")}. O texto fica guardado com a reunião, e é ele que a ata cita.`}
        data-testid="arquivo-da-transcricao"
      />

      <CampoLongo
        rotulo="…ou cole a transcrição"
        rows={4}
        value={texto}
        onChange={(evento) => setTexto(evento.target.value)}
        ajuda="O texto colado é guardado do mesmo jeito que o arquivo."
        data-testid="texto-da-transcricao"
      />

      {/* A duração em MINUTOS, que é como quem operou a reunião a conhece. O contrato da API é
          em segundos (`duracao_seg`), e a conversão é desta tela: um fator 60 esquecido
          transforma uma reunião de 1h30 em 90 segundos e ninguém vê. */}
      <Campo
        rotulo="Duração da reunião em minutos (opcional)"
        type="number"
        min={1}
        value={duracao}
        onChange={(evento) => setDuracao(evento.target.value)}
        data-testid="duracao-da-transcricao"
      />

      {envio.isError ? (
        <Aviso tom="erro" className="mt-3" data-testid="erro-da-transcricao">
          {mensagemDoConsole(envio.error)}
        </Aviso>
      ) : null}

      <div className="acoes-do-console mt-3">
        <Botao
          disabled={envio.isPending}
          data-testid="confirmar-transcricao"
          onClick={() => {
            const escolhido = arquivo.current?.files?.[0] ?? null;
            const paraEnviar = escolhido ?? (texto.trim() ? arquivoDeTranscricaoColada(texto) : null);
            if (!paraEnviar) return;
            envio.mutate(
              { tipo: "transcricao", arquivo: paraEnviar, duracaoSeg },
              {
                onSuccess: () => {
                  setTexto("");
                  setDuracao("");
                  if (arquivo.current) arquivo.current.value = "";
                },
              },
            );
          }}
        >
          {envio.isPending ? "Enviando…" : "Enviar transcrição"}
        </Botao>
      </div>
    </Cartao>
  );
}

function ArquivoDaReuniao({ reuniaoId }: { reuniaoId: string }) {
  const [tipo, setTipo] = useState<"gravacao" | "audio">("audio");
  const arquivo = useRef<HTMLInputElement>(null);
  const envio = useEnviarMidia(reuniaoId);
  const teto = Math.round(TAMANHO_MAXIMO_DA_MIDIA_EM_BYTES / (1024 * 1024));

  const extensoes = tipo === "audio" ? EXTENSOES_DE_AUDIO : EXTENSOES_DE_GRAVACAO;

  return (
    <Cartao titulo="Gravação ou áudio" data-testid="enviar-arquivo-da-reuniao">
      <p className="cartao__texto">
        O arquivo fica guardado com a reunião. <b>Um vídeo de 1h30 não cabe:</b> o limite é {teto}{" "}
        MB. Extraia o áudio antes de subir — ou suba direto a transcrição, ao lado.
      </p>

      <div className="acoes-do-console mb-3">
        <Botao
          variante={tipo === "audio" ? "solido" : "fantasma"}
          tamanho="pequeno"
          onClick={() => setTipo("audio")}
          data-testid="tipo-audio"
        >
          Áudio
        </Botao>
        <Botao
          variante={tipo === "gravacao" ? "solido" : "fantasma"}
          tamanho="pequeno"
          onClick={() => setTipo("gravacao")}
          data-testid="tipo-gravacao"
        >
          Vídeo da gravação
        </Botao>
      </div>

      <Campo
        rotulo={tipo === "audio" ? "Arquivo de áudio" : "Arquivo de vídeo"}
        type="file"
        accept={extensoes.join(",")}
        ref={arquivo}
        ajuda={`Aceita ${extensoes.join(", ")}.`}
        data-testid="arquivo-da-midia"
      />

      {envio.isError ? (
        <Aviso tom="erro" className="mt-3" data-testid="erro-do-arquivo">
          {mensagemDoConsole(envio.error)}
        </Aviso>
      ) : null}

      <div className="acoes-do-console mt-3">
        <Botao
          variante="fantasma"
          disabled={envio.isPending}
          data-testid="confirmar-arquivo"
          onClick={() => {
            const escolhido = arquivo.current?.files?.[0] ?? null;
            if (!escolhido) return;
            envio.mutate(
              { tipo, arquivo: escolhido },
              {
                onSuccess: () => {
                  if (arquivo.current) arquivo.current.value = "";
                },
              },
            );
          }}
        >
          {envio.isPending ? "Enviando…" : "Enviar arquivo"}
        </Botao>
      </div>
    </Cartao>
  );
}

/* ------------------------------------------------------------ o que ainda não funciona */

/**
 * O estado do pipeline, com o motivo de cada etapa que não está de pé.
 *
 * `disponivel: false` não é erro — é o estado declarado desta implantação, e vem do servidor
 * junto da listagem justamente para a tela não desenhar uma barra de progresso que nunca anda.
 */
function ProcessamentoAutomatico({ pipeline }: { pipeline: EstadoDoPipeline }) {
  return (
    <Cartao
      className="mb-4"
      titulo="Processamento automático"
      data-testid="pipeline-de-midia"
      data-disponivel={pipeline.disponivel ? "sim" : "nao"}
      acessorio={
        <Chip estado={pipeline.disponivel ? "ok" : "neutro"}>
          {pipeline.disponivel ? "disponível" : "indisponível"}
        </Chip>
      }
    >
      <ul className="etapas-do-pipeline">
        {pipeline.etapas.map((etapa) => (
          <li
            key={etapa.nome}
            className="etapas-do-pipeline__item"
            data-disponivel={etapa.disponivel ? "sim" : "nao"}
            data-testid={`etapa-${etapa.nome}`}
          >
            <span className="etapas-do-pipeline__marca" aria-hidden="true">
              {etapa.disponivel ? "✓" : "—"}
            </span>
            <span>
              <b>{rotuloDaEtapa(etapa.nome)}</b>
              {etapa.motivo ? <> — {etapa.motivo}</> : null}
            </span>
          </li>
        ))}
      </ul>

      {!pipeline.disponivel ? (
        <Aviso tom="marca" className="mt-3" data-testid="caminho-manual">
          <b>Enquanto isso, use o caminho acima.</b> {pipeline.caminho_manual}
        </Aviso>
      ) : null}

    </Cartao>
  );
}

/* --------------------------------------------------------------------- o que já existe */

function ListaDeMidias({
  reuniaoId,
  midias,
  pipeline,
  podeEditar,
}: {
  reuniaoId: string;
  midias: readonly Midia[];
  pipeline: EstadoDoPipeline;
  podeEditar: boolean;
}) {
  if (midias.length === 0) {
    return (
      <Aviso tom="marca" data-testid="sem-midia">
        Nenhum material desta reunião foi enviado até agora. A ata pode ser escrita sem ele.
      </Aviso>
    );
  }

  return (
    <div className="lista-de-itens" data-testid="midias-da-reuniao">
      {midias.map((midia) => (
        <CartaoDaMidia
          key={midia.id}
          reuniaoId={reuniaoId}
          midia={midia}
          pipeline={pipeline}
          podeEditar={podeEditar}
        />
      ))}
    </div>
  );
}

const CHIP_DO_ESTADO: Record<string, "ok" | "atencao" | "erro" | "neutro" | "aguardando"> = {
  concluida: "ok",
  processando: "aguardando",
  pendente: "neutro",
  falhou: "erro",
};

function CartaoDaMidia({
  reuniaoId,
  midia,
  pipeline,
  podeEditar,
}: {
  reuniaoId: string;
  midia: Midia;
  pipeline: EstadoDoPipeline;
  podeEditar: boolean;
}) {
  const [lendo, setLendo] = useState(false);
  const enfileirar = useEnfileirarDerivacao(reuniaoId);
  const reprocessar = useReprocessarMidia(reuniaoId);
  const remover = useRemoverMidia(reuniaoId);
  const travada = midiaTravada(midia);
  const erroDeAcao =
    (enfileirar.isError && mensagemDoConsole(enfileirar.error)) ||
    (reprocessar.isError && mensagemDoConsole(reprocessar.error)) ||
    (remover.isError && mensagemDoConsole(remover.error)) ||
    null;

  return (
    <Cartao
      titulo={`${rotuloDoTipoDeMidia(midia.tipo)}${midia.nome_original ? ` · ${midia.nome_original}` : ""}`}
      data-testid={`midia-${midia.id}`}
      data-estado={midia.estado}
      acessorio={
        <Chip estado={travada ? "erro" : (CHIP_DO_ESTADO[midia.estado] ?? "neutro")}>
          {travada ? "travada" : rotuloDoEstadoDaMidia(midia.estado)}
        </Chip>
      }
    >
      <p className="nota-rodape">
        {midia.estado === "processando" && midia.iniciado_em ? (
          /* DESDE QUANDO é o ponto: mídia presa em `processando` para sempre é o estado que
             ninguém enxerga, e sem a data ela é indistinguível de uma que começou agora. */
          <>
            Processando desde{" "}
            <span className="numero-tabular">{formatarQuando(midia.iniciado_em)}</span>
            {" · "}
          </>
        ) : null}
        {midia.origem === "upload" ? "Enviada" : "Gerada pelo portal"} em{" "}
        <span className="numero-tabular">{formatarQuando(midia.criado_em) ?? "—"}</span>
        {midia.enviado_por ? ` por ${midia.enviado_por.nome}` : ""}
        {midia.tamanho !== null ? ` · ${formatarTamanho(midia.tamanho)}` : ""}
        {midia.caracteres !== null
          ? ` · ${midia.caracteres.toLocaleString("pt-BR")} caracteres`
          : ""}
        {midia.duracao_seg !== null ? ` · ${Math.round(midia.duracao_seg / 60)} min de reunião` : ""}
        {midia.tentativas > 0 ? ` · ${midia.tentativas} tentativa(s)` : ""}
      </p>

      {midia.erro ? (
        /* O motivo do servidor, palavra por palavra. "Falhou" sem motivo manda o consultor abrir
           chamado; "o processamento falhou 3 vezes e o portal parou de tentar — envie a
           transcrição pronta" manda ele resolver. */
        <Aviso tom="atencao" className="mt-3" data-testid={`erro-da-midia-${midia.id}`}>
          {midia.erro}
        </Aviso>
      ) : null}

      {travada ? (
        <Aviso tom="erro" className="mt-3" data-testid={`midia-travada-${midia.id}`}>
          <b>Este arquivo está &ldquo;processando&rdquo; há mais de 30 minutos.</b> Na prática,
          parou. Reprocesse para tentar de novo.
        </Aviso>
      ) : null}

      {midia.tipo === "transcricao" && midia.estado === "concluida" ? (
        <div className="mt-3">
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => setLendo((valor) => !valor)}
            data-testid={`ler-transcricao-${midia.id}`}
          >
            {lendo ? "Fechar a transcrição" : "Ler a transcrição"}
          </Botao>
          {lendo ? <TextoDaMidia midiaId={midia.id} /> : null}
        </div>
      ) : null}

      {/* O arquivo de volta. Não é conforto: sem esta rota a gravação era escrita sem leitura —
          o único consumidor possível é a derivação, e ela recusa enquanto o pipeline não estiver
          de pé. Quem sobe a gravação hoje precisa conseguir ouvi-la para escrever a ata à mão,
          que é o caminho que funciona. O servidor serve sempre como binário. */}
      {midia.estado === "concluida" && midia.tipo !== "transcricao" ? (
        <p className="nota-rodape mt-2">
          <a href={urlDoArquivoDaMidia(midia.id)} data-testid={`baixar-midia-${midia.id}`}>
            Baixar o arquivo
          </a>
        </p>
      ) : null}

      {podeEditar ? (
        <div className="item-da-ata__acoes">
          {/* O botão existe e a recusa é lida: `enfileirar_derivacao` responde 409 com o motivo
              REAL — `ffmpeg` ausente, credencial do gateway ausente —, e é esse texto que aparece
              aqui embaixo. Esconder o botão esconderia a explicação junto com ele; o que não pode
              existir é a fila que engole a mídia em silêncio. */}
          {midia.tipo === "gravacao" ? (
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              disabled={enfileirar.isPending}
              onClick={() => enfileirar.mutate({ midiaId: midia.id, tipo: "audio" })}
              data-testid={`derivar-audio-${midia.id}`}
            >
              Extrair o áudio
            </Botao>
          ) : null}
          {midia.tipo === "audio" ? (
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              disabled={enfileirar.isPending}
              onClick={() => enfileirar.mutate({ midiaId: midia.id, tipo: "transcricao" })}
              data-testid={`derivar-transcricao-${midia.id}`}
            >
              Transcrever
            </Botao>
          ) : null}
          {midia.estado === "falhou" || travada ? (
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              disabled={reprocessar.isPending}
              onClick={() => reprocessar.mutate(midia.id)}
              data-testid={`reprocessar-${midia.id}`}
            >
              {reprocessar.isPending ? "Reprocessando…" : "Reprocessar"}
            </Botao>
          ) : null}
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            disabled={remover.isPending}
            onClick={() => remover.mutate(midia.id)}
            data-testid={`remover-midia-${midia.id}`}
          >
            {remover.isPending ? "Removendo…" : "Remover"}
          </Botao>
        </div>
      ) : null}

      {erroDeAcao ? (
        /* A recusa de enfileirar é o texto mais útil desta tela: ela diz o motivo real —
           `ffmpeg` ausente, credencial do gateway ausente — em vez de um código de status. */
        <Aviso tom="atencao" className="mt-3" data-testid={`recusa-da-midia-${midia.id}`}>
          {erroDeAcao}
        </Aviso>
      ) : null}

      {!pipeline.disponivel && midia.tipo !== "transcricao" ? (
        <p className="nota-rodape mt-2">
          Extrair o áudio e transcrever não funcionam enquanto o processamento automático estiver
          indisponível.
        </p>
      ) : null}
    </Cartao>
  );
}

/** O texto da transcrição — o único lugar do portal onde ele aparece. */
function TextoDaMidia({ midiaId }: { midiaId: string }) {
  const midia = useMidia(midiaId);

  if (midia.isPending) {
    return (
      <p className="nota-rodape mt-2" role="status">
        Carregando a transcrição…
      </p>
    );
  }
  if (midia.isError) {
    return (
      <Aviso tom="erro" className="mt-2">
        {mensagemDoConsole(midia.error)}
      </Aviso>
    );
  }

  return (
    <pre className="transcricao mt-2" data-testid={`transcricao-${midiaId}`}>
      {midia.data.texto ?? "— (sem texto)"}
    </pre>
  );
}
